import { useEffect, useState } from "react";
import { BarChart3, Bot, Globe, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

const ROLE_TR = { admin: "Admin", supplier: "Tedarikçi", buyer: "Mağaza" };

const Toggle = ({ checked, onChange, icon: Icon, title, desc, tid }) => (
    <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-colors ${checked ? "border-emerald-300 bg-emerald-50" : "border-stone-200 bg-muted/40"}`}
        data-testid={tid}
    >
        <Icon className={`h-5 w-5 shrink-0 ${checked ? "text-emerald-600" : "text-stone-400"}`} />
        <div className="flex-1">
            <p className="text-sm font-semibold">{title}</p>
            <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
        <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-stone-300"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
        </span>
    </button>
);

export default function AiTab() {
    const [cfg, setCfg] = useState(null);
    const [stats, setStats] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => {
        api.get("/ai/admin/settings").then((r) => setCfg(r.data)).catch(() => {});
        api.get("/ai/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/ai/admin/settings", cfg);
            toast.success("AI ayarları kaydedildi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setBusy(false);
        }
    };

    if (!cfg) return <div className="h-40 animate-pulse rounded-2xl bg-muted" />;

    const panelTotal = stats ? Object.values(stats.by_role).reduce((a, b) => a + b.messages, 0) : 0;

    return (
        <div className="space-y-4" data-testid="admin-ai-tab">
            <div className="grid gap-3 md:grid-cols-2">
                <Toggle
                    checked={cfg.panel_enabled}
                    onChange={(v) => setCfg({ ...cfg, panel_enabled: v })}
                    icon={Sparkles}
                    title="Panel Asistanı (Angora AI)"
                    desc="Admin, tedarikçi ve mağaza panellerindeki yapay zeka asistanı"
                    tid="ai-toggle-panel"
                />
                <Toggle
                    checked={cfg.public_enabled}
                    onChange={(v) => setCfg({ ...cfg, public_enabled: v })}
                    icon={Globe}
                    title="ATA AI (Herkese Açık)"
                    desc="Giriş yapmamış ziyaretçilere platformu tanıtan asistan"
                    tid="ai-toggle-public"
                />
            </div>

            <div className="rounded-2xl border bg-card p-5">
                <div className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-terra-600" />
                    <h3 className="font-display font-bold">Özel Yönetim Talimatları</h3>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                    Buraya yazdığınız kurallar tüm asistanların (panel + ATA AI) sistem talimatına eklenir. Örn: güncel kampanya duyurusu, iletişim yönlendirmesi, yanıt üslubu kuralları.
                </p>
                <Textarea
                    rows={4}
                    value={cfg.custom_instructions}
                    onChange={(e) => setCfg({ ...cfg, custom_instructions: e.target.value })}
                    placeholder="Örn: Bu ay Antep fıstığında %5 kampanya olduğundan bahset. Fiyat sorularında KDV dahil bilgisi ver..."
                    className="mt-3"
                    data-testid="ai-custom-instructions"
                />
                <div className="mt-3 flex justify-end">
                    <Button onClick={save} disabled={busy} className="bg-terra-600 text-white hover:bg-terra-700" data-testid="ai-settings-save">
                        <Save className="mr-2 h-4 w-4" /> {busy ? "Kaydediliyor..." : "Ayarları Kaydet"}
                    </Button>
                </div>
            </div>

            <div className="rounded-2xl border bg-card p-5" data-testid="ai-stats-card">
                <div className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-terra-600" />
                    <h3 className="font-display font-bold">Kullanım İstatistikleri</h3>
                </div>
                {!stats ? (
                    <div className="mt-3 h-20 animate-pulse rounded-xl bg-muted" />
                ) : (
                    <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="rounded-xl border p-4" data-testid="ai-stat-total">
                            <p className="font-display text-2xl font-extrabold text-terra-600">{panelTotal}</p>
                            <p className="text-xs text-muted-foreground">Panel asistanı mesajı</p>
                        </div>
                        <div className="rounded-xl border p-4" data-testid="ai-stat-users">
                            <p className="font-display text-2xl font-extrabold text-terra-600">{stats.unique_users}</p>
                            <p className="text-xs text-muted-foreground">Asistan kullanan üye</p>
                        </div>
                        <div className="rounded-xl border p-4" data-testid="ai-stat-public">
                            <p className="font-display text-2xl font-extrabold text-terra-600">{stats.public_messages}</p>
                            <p className="text-xs text-muted-foreground">ATA AI mesajı (ziyaretçi)</p>
                        </div>
                        <div className="rounded-xl border p-4" data-testid="ai-stat-roles">
                            <p className="text-xs font-semibold">Rol dağılımı</p>
                            <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                                {Object.entries(stats.by_role).map(([r, v]) => (
                                    <p key={r}>{ROLE_TR[r] || r}: {v.messages} mesaj</p>
                                ))}
                                {!Object.keys(stats.by_role).length && <p>Henüz mesaj yok</p>}
                            </div>
                        </div>
                    </div>
                )}
                {stats?.public_last && <p className="mt-3 text-[11px] text-muted-foreground" data-testid="ai-stat-last">Son ATA AI aktivitesi: {fmtDate(stats.public_last)}</p>}
            </div>
        </div>
    );
}
